import React, { useState } from "react";
import {
  Box,
  Typography,
  Fade,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  IconButton,
  Chip,
  Pagination,
  Skeleton,
  Collapse,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ContentCopy, Print, Edit, FilterList, FilterListOff } from "@mui/icons-material";
import { Search, Filter, Trash2, Camera as CameraIconL } from "lucide-react";
import { ColumnVisibilityButton } from "../../shared/components/ColumnVisibilityButton";
import { SortableHeader } from "../../shared/components/SortableHeader";
import { MobileSortControl } from "../../shared/components/MobileSortControl";
import { PageHeader } from "../../shared/components/PageHeader";
import { EmptyState } from "../../shared/components/EmptyState";
import { getSystemDetails, formatToIST } from "../../utils/formatting";
import { searchOptions } from "../../utils/licenseStatus";
import { getStatusColorSx } from "../../theme/theme";

function CamerasView({
  darkMode,
  theme,
  systems,
  searchTerm,
  handleSearch,
  searchType,
  setSearchType,
  sortConfig,
  handleSort,
  visibleColumns,
  setVisibleColumns,
  toggleColumn,
  columnMenuAnchor,
  activeColumnTable,
  handleColumnMenuOpen,
  handleColumnMenuClose,
  page,
  handlePageChange,
  rowsPerPage,
  isMobile,
  loading,
  filteredCameras,
  paginatedCameras,
  copiedField,
  handleCopy,
  handleOpenDialog,
  handleEditCamera,
  handlePrint,
  deleteCamera,
}) {
  const [showFilters, setShowFilters] = useState(true);

  return (
    <Fade in={true} timeout={500}>
      <Box key="cameras-view">
        <PageHeader
          title="Camera Management"
          subtitle="Monitor and configure your cameras"
          darkMode={darkMode}
          actions={
            <>
              <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
                <Button
                  variant="outlined"
                  size="small"
                  color={showFilters ? "primary" : "inherit"}
                  startIcon={showFilters ? <FilterListOff fontSize="small" /> : <FilterList fontSize="small" />}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  Filters
                </Button>
              </Tooltip>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Print />}
                onClick={() => handlePrint('cameras')}
              >
                Print
              </Button>
              <Button
                variant="contained"
                startIcon={<CameraIconL size={18} />}
                onClick={() => handleOpenDialog("camera")}
                sx={{ px: 4, py: 1.5 }}
              >
                Add New Camera
              </Button>
              <ColumnVisibilityButton
                tableType="cameras"
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                toggleColumn={toggleColumn}
                columnMenuAnchor={columnMenuAnchor}
                activeColumnTable={activeColumnTable}
                handleColumnMenuOpen={handleColumnMenuOpen}
                handleColumnMenuClose={handleColumnMenuClose}
                theme={theme}
              />
            </>
          }
        />

        <Collapse in={showFilters}>
          <Card sx={{ mb: 3, bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <Filter size={20} color={darkMode ? '#93A2BC' : theme.palette.text.secondary} />
                  <Typography variant="body2" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary', fontWeight: 500 }}>
                    Filter by:
                  </Typography>
                </Box>
                <TextField
                  variant="outlined"
                  placeholder={`Search by ${searchOptions.cameras.find(o => o.value === searchType)?.label || 'System UID'}`}
                  value={searchTerm}
                  onChange={handleSearch}
                  size="small"
                  sx={{ minWidth: 300 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={18} />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Search By</InputLabel>
                  <Select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                    label="Search By"
                  >
                    {searchOptions.cameras.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </Collapse>

        {loading.cameras ? (
          <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            {isMobile ? (
              <CardContent>
                {[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={80} sx={{ mb: 1.5 }} />)}
              </CardContent>
            ) : (
              <TableContainer>
                <Table sx={{ minWidth: 1200 }}>
                  <TableBody>
                    {[...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(7)].map((__, j) => (
                          <TableCell key={j}><Skeleton variant="text" /></TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        ) : isMobile ? (
          <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            <CardContent sx={{ p: 2 }}>
              <MobileSortControl
                tableType="cameras"
                currentConfig={sortConfig.cameras}
                handleSort={handleSort}
                options={[
                  { value: 'id', label: 'ID' },
                  { value: 'uuid', label: 'System UID' },
                  { value: 'project_id', label: 'Project' },
                  { value: 'camera_serial', label: 'Camera Serial' },
                  { value: 'status', label: 'Status' },
                  { value: 'registered_at', label: 'Registered At' },
                ]}
              />
              {paginatedCameras.map((camera) => {
                const systemDetails = getSystemDetails(systems, camera.uuid);
                return (
                  <Box
                    key={camera.id}
                    sx={{ p: 2, mb: 1.5, borderRadius: 2, bgcolor: darkMode ? '#16213A' : '#EAF3FB', border: darkMode ? '1px solid #24304A' : '1px solid #D8E1E8' }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                        {camera.camera_serial}
                      </Typography>
                      <Chip
                        size="small"
                        label={camera.status}
                        sx={camera.status === 'online' ? getStatusColorSx('active', darkMode) : getStatusColorSx('offline', darkMode)}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: darkMode ? '#93A2BC' : 'text.secondary', fontFamily: 'monospace', mb: 1 }}>
                      {camera.uuid?.substring(0, 16)}...
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Chip size="small" label={systemDetails?.project_id || 'N/A'} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 600 }} />
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: darkMode ? '#93A2BC' : 'text.secondary', mb: 1.5 }}>
                      Registered {formatToIST(camera.registered_at)}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <IconButton color="primary" onClick={() => handleEditCamera(camera)} size="small">
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton color="error" onClick={() => deleteCamera(camera.id)} size="small">
                        <Trash2 size={18} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
              {filteredCameras.length === 0 && (
                <EmptyState darkMode={darkMode} title="No cameras found" subtitle="Try adjusting your search or add a new camera" />
              )}
              {filteredCameras.length > rowsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                  <Pagination count={Math.ceil(filteredCameras.length / rowsPerPage)} page={page} onChange={handlePageChange} color="primary" size="medium" />
                </Box>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow>
                    {visibleColumns.cameras.id && (
                      <SortableHeader label="ID" tableType="cameras" columnKey="id" currentConfig={sortConfig.cameras} handleSort={handleSort} />
                    )}
                    {visibleColumns.cameras.uuid && (
                      <SortableHeader label="System UID" tableType="cameras" columnKey="uuid" currentConfig={sortConfig.cameras} handleSort={handleSort} />
                    )}
                    {visibleColumns.cameras.project_id && (
                      <SortableHeader label="Project" tableType="cameras" columnKey="project_id" currentConfig={sortConfig.cameras} handleSort={handleSort} />
                    )}
                    {visibleColumns.cameras.camera_serial && (
                      <SortableHeader label="Camera Serial" tableType="cameras" columnKey="camera_serial" currentConfig={sortConfig.cameras} handleSort={handleSort} />
                    )}
                    {visibleColumns.cameras.status && (
                      <SortableHeader label="Status" tableType="cameras" columnKey="status" currentConfig={sortConfig.cameras} handleSort={handleSort} />
                    )}
                    {visibleColumns.cameras.registered_at && (
                      <SortableHeader label="Registered At" tableType="cameras" columnKey="registered_at" currentConfig={sortConfig.cameras} handleSort={handleSort} />
                    )}
                    {visibleColumns.cameras.actions && (
                      <TableCell align="center">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCameras.map((camera) => {
                    const systemDetails = getSystemDetails(systems, camera.uuid);

                    return (
                      <TableRow key={camera.id} hover>
                        {visibleColumns.cameras.id && (
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                              {camera.id}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleColumns.cameras.uuid && (
                          <TableCell>
                            <Tooltip title={copiedField === `cam-${camera.id}` ? "Copied!" : camera.uuid}>
                              <Box
                                onClick={() => handleCopy(camera.uuid, `cam-${camera.id}`)}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                              >
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                                  {camera.uuid.substring(0, 8)}...
                                </Typography>
                                <ContentCopy sx={{ fontSize: 14, opacity: 0.5, color: copiedField === `cam-${camera.id}` ? 'success.main' : 'inherit' }} />
                              </Box>
                            </Tooltip>
                          </TableCell>
                        )}
                        {visibleColumns.cameras.project_id && (
                          <TableCell>
                            <Chip
                              size="small"
                              label={systemDetails?.project_id || 'N/A'}
                              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 600 }}
                            />
                          </TableCell>
                        )}
                        {visibleColumns.cameras.camera_serial && (
                          <TableCell>
                            <Typography variant="body2" sx={{ color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                              {camera.camera_serial}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleColumns.cameras.status && (
                          <TableCell>
                            <Chip
                              size="small"
                              label={camera.status}
                              sx={camera.status === 'online' ? getStatusColorSx('active', darkMode) : getStatusColorSx('offline', darkMode)}
                            />
                          </TableCell>
                        )}
                        {visibleColumns.cameras.registered_at && (
                          <TableCell>
                            <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                              {formatToIST(camera.registered_at)}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleColumns.cameras.actions && (
                          <TableCell align="center">
                            <Tooltip title="Edit Camera">
                              <IconButton
                                color="primary"
                                onClick={() => handleEditCamera(camera)}
                                size="small"
                                sx={{ mr: 1 }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Camera">
                              <IconButton
                                color="error"
                                onClick={() => deleteCamera(camera.id)}
                                size="small"
                              >
                                <Trash2 size={18} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredCameras.length > rowsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Pagination
                  count={Math.ceil(filteredCameras.length / rowsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size={isMobile ? 'medium' : 'large'}
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}

            {filteredCameras.length === 0 && (
              <EmptyState darkMode={darkMode} title="No cameras found" subtitle="Try adjusting your search or add a new camera" />
            )}
          </Card>
        )}
      </Box>
    </Fade>
  );
}

export default CamerasView;
